import React, { useEffect, useState } from "react";
import {
  Dialog,
  Typography,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const ReportDialog = ({ open, onClose, resultIds }) => {
    const apiUrl = process.env.REACT_APP_API_URL;
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  useEffect(() => {
    if (open && resultIds && resultIds.length > 0) {
      setLoading(true);
      axios
        .post(
          apiUrl+"/api/result/bulk",
          resultIds, // sending list of resultIds in request body
          {
            headers: {
              "Content-Type": "application/json",
        Accept: "*/*",
        "Access-Control-Allow-Origin": "*",
        Authorization: getBasicAuthHeader(),
            },
          }
        )
        .then((res) => {
          generatePdf(res.data);
        })
        .catch((err) => {
          console.error("Error fetching report:", err);
        })
        .finally(() => {
          setLoading(false);
          onClose()
        });
    }
  }, [open, resultIds]);

  
const createRingGraph = (percent, size = 60) => {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");

  const center = size / 2;
  const radius = size / 2 - 6;

  // background shadow ring
  ctx.strokeStyle = "#f0f0f0";
  ctx.lineWidth = 8;
  ctx.beginPath();
  ctx.arc(center, center, radius, 0, Math.PI * 2);
  ctx.stroke();

  // progress arc
  ctx.strokeStyle =
    percent >= 70 ? "#27ae60" : percent >= 40 ? "#f39c12" : "#c0392b";
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.arc(
    center,
    center,
    radius,
    -Math.PI / 2,
    (Math.PI * 2 * percent) / 100 - Math.PI / 2
  );
  ctx.stroke();

  // percentage text
  ctx.fillStyle = "#2c3e50";
  ctx.font = "bold 12px Arial";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(`${percent}%`, center, center);

  return canvas.toDataURL("image/png");
};

const generatePdf = (data) => {
  const doc = new jsPDF("p", "pt", "a4");
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // group by userId
  const groupedByUser = data.reduce((acc, item) => {
    if (!acc[item.userId]) acc[item.userId] = [];
    acc[item.userId].push(item);
    return acc;
  }, {});

  Object.keys(groupedByUser).forEach((userId, idx) => {
    if (idx !== 0) doc.addPage();

    const userResults = groupedByUser[userId];
    const first = userResults[0];

    // calculate overall average %
    let allPercents = [];
    userResults.forEach((res) => {
      res.domainPerformance.forEach((d) => {
        allPercents.push(
          Math.round((d.correctAnswers / d.totalQuestion) * 100)
        );
      });
    });
    const avgPercent =
      allPercents.length > 0
        ? Math.round(allPercents.reduce((a, b) => a + b, 0) / allPercents.length)
        : 0;

    // header
    doc.setFontSize(22).setTextColor(128, 0, 128).setFont("helvetica", "bold");
    const title = "Course Report Card";
    doc.text(title, (pageW - doc.getTextWidth(title)) / 2, 50);

    // brand
    doc.setFontSize(12).setFont("helvetica", "bold");
    const brandX =
      pageW - (doc.getTextWidth("Study") + doc.getTextWidth("AK.ai")) - 40;
    doc.setTextColor(128, 0, 128).text("Study", brandX, 40);
    doc.setTextColor(0, 150, 0).text("AK.ai", brandX + doc.getTextWidth("Study"), 40);

    // line
    doc.setDrawColor(128, 0, 128).setLineWidth(1);
    doc.line(40, 60, pageW - 40, 60);

    // student info box
    doc.setFillColor(240, 240, 255);
    doc.roundedRect(40, 70, pageW - 80, 70, 6, 6, "F");
    doc.setFontSize(12).setTextColor(0, 0, 0);
    doc.text(`Student: ${first.firstName} ${first.lastName}`, 50, 95);
    doc.text(`Email: ${first.email || "-"}`, 50, 115);
    doc.setTextColor(0, 100, 0).setFont("helvetica", "bold");
    doc.text(`Overall Average: ${avgPercent}%`, 300, 105);

    let currentY = 160;

    // group results by course
    const groupedByCourse = userResults.reduce((acc, item) => {
      if (!acc[item.courseName]) acc[item.courseName] = [];
      acc[item.courseName].push(item);
      return acc;
    }, {});

    Object.keys(groupedByCourse).forEach((courseName) => {
      // course header
      doc.setFillColor(230, 255, 230);
      doc.roundedRect(40, currentY - 10, pageW - 80, 25, 4, 4, "F");
      doc.setFontSize(14).setTextColor(0, 150, 0).setFont("helvetica", "bold");
      doc.text(`Course: ${courseName}`, 50, currentY + 6);
      currentY += 40;

      const courseResults = groupedByCourse[courseName];

      courseResults.forEach((res) => {
        // --- Test Card Background ---
        const numDomains = res.domainPerformance.length;
        const baseHeight = 180;
        const testCardHeight = baseHeight + 80; // leave extra for domain names

        // page break if overflowing
        if (currentY + testCardHeight > pageH - 60) {
          doc.addPage();
          currentY = 60;
        }

        doc.setFillColor(255, 255, 255);
        doc.roundedRect(40, currentY, pageW - 80, testCardHeight, 6, 6, "S");

        // --- Highlight Test Header Row ---
        doc.setFillColor(230, 220, 255);
        doc.rect(40, currentY, pageW - 80, 30, "F");

        doc.setFontSize(12).setTextColor(40, 40, 40).setFont("helvetica", "bold");
        doc.text(`Test: ${res.testName}`, 50, currentY + 20);

        // analytics
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10).setTextColor(60, 60, 60);
        const analytic = res.analyticData.map((a) => `${a.key}: ${a.data}`).join(", ");
        doc.text(`Analytics: ${analytic || "-"}`, 50, currentY + 50);

        // --- Domain Performance graphs ---
        doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(80, 80, 80);
        doc.text("Domain Performance:", 50, currentY + 75);

        const availableW = pageW - 120;
        const graphSize = 60;
        const spacing = availableW / numDomains;
        const offsetY = currentY + 95;

        res.domainPerformance.forEach((d, i) => {
          const percent = Math.round((d.correctAnswers / d.totalQuestion) * 100);
          const img = createRingGraph(percent, graphSize);

          const offsetX = 60 + i * spacing + spacing / 2 - graphSize / 2;
          doc.addImage(img, "PNG", offsetX, offsetY, graphSize, graphSize);

          // --- Wrap long domain names ---
          const domainLines = doc.splitTextToSize(d.domainName, graphSize + 20);
          doc.setFontSize(9).setTextColor(60, 60, 60);
          doc.text(domainLines, offsetX + graphSize / 2, offsetY + graphSize + 12, {
            align: "center",
          });
        });

        currentY += testCardHeight + 30; // space after test
      });

      currentY += 20;
    });

    // footer
    doc.setFontSize(9).setTextColor(100, 100, 100);
    doc.text(
      "Generated by StudyAK.ai • Confidential Report",
      pageW / 2,
      pageH - 20,
      { align: "center" }
    );
  });

  doc.save("report.pdf");
};

  return (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogContent
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 30,
        }}
      >
        <CircularProgress />
        <Typography variant="subtitle1" style={{ marginTop: 15 }}>
          Report is getting generated...
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default ReportDialog;
